import { useSubmit, useSearchParams } from '@remix-run/react';
import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check, X, LayoutGrid, List } from 'lucide-react';

export type FacetValueResult = {
  facetValue: {
    id: string;
    code: string;
    name: string;
    facet: {
      id: string;
      code: string;
      name: string;
    };
  };
  count: number;
};

interface CategoryFilterBarProps {
  orderedCount?: number;
  facetValues?: FacetValueResult[];
  activeFacetValueIds?: string[];
  viewMode: 'grid' | 'list';
  setViewMode: (mode: 'grid' | 'list') => void;
}

export function CategoryFilterBar({
  orderedCount = 0,
  facetValues = [],
  activeFacetValueIds = [],
  viewMode,
  setViewMode,
}: CategoryFilterBarProps) {
  const submit = useSubmit();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const sort = searchParams.get('sort') || '';

  // Read specific filter params
  const discountFvidParam = searchParams.get('discount_fvid');
  const bulkFvidParam = searchParams.get('bulk_fvid');

  // Find Facet Values by Code
  const discountFV = facetValues.find(
    (fv) => fv.facetValue.code === 'discounted',
  );
  const bulkFV = facetValues.find(
    (fv) => fv.facetValue.code === 'bulk-pricing',
  );

  // Filter for 'Origin' facet (code: origin)
  const originFacetValues = facetValues.filter(
    (fv) => fv.facetValue.facet.code === 'origin',
  );
  const supplierFacetValues = facetValues.filter(
    (fv) => fv.facetValue.facet.code === 'supplier',
  );

  const [originOpen, setOriginOpen] = useState(false);
  const originButtonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (originOpen && originButtonRef.current) {
      const rect = originButtonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  }, [originOpen]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        originOpen &&
        originButtonRef.current &&
        !originButtonRef.current.contains(e.target as Node)
      ) {
        const dropdown = document.getElementById('origin-dropdown-portal');
        if (dropdown && !dropdown.contains(e.target as Node)) {
          setOriginOpen(false);
        }
      }
    };
    window.addEventListener('click', handleClick, true);
    return () => window.removeEventListener('click', handleClick, true);
  }, [originOpen]);

  useEffect(() => {
    const handleScroll = () => {
      if (originOpen) setOriginOpen(false);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [originOpen]);

  const handleFilterChange = (key: string, value: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === null) {
      newParams.delete(key);
    } else {
      newParams.set(key, value);
    }
    // Check page reset
    newParams.set('page', '1');
    submit(newParams);
  };

  const toggleFilterParam = (
    key: string,
    id: string,
    currentId: string | null,
  ) => {
    // Toggle: if currentId matches id, remove it (set to null). Else set to id.
    handleFilterChange(key, currentId === id ? null : id);
  };

  const toggleFacetValue = (id: string) => {
    const newParams = new URLSearchParams(searchParams);
    const currentIds = newParams.getAll('fvid');

    if (currentIds.includes(id)) {
      const newIds = currentIds.filter((cid) => cid !== id);
      newParams.delete('fvid');
      newIds.forEach((nid) => newParams.append('fvid', nid));
    } else {
      newParams.append('fvid', id);
    }
    newParams.set('page', '1');
    submit(newParams);
  };

  // Calculate generic active count (only from fvid list)
  const activeOriginCount = activeFacetValueIds.filter((id) =>
    originFacetValues.some((fv) => fv.facetValue.id === id),
  ).length;
  const activeSupplierIds = activeFacetValueIds.filter((id) =>
    supplierFacetValues.some((fv) => fv.facetValue.id === id),
  );

  const clearSupplierFilter = () => {
    const newParams = new URLSearchParams(searchParams);
    const retainedIds = newParams
      .getAll('fvid')
      .filter(
        (id) => !supplierFacetValues.some((fv) => fv.facetValue.id === id),
      );
    newParams.delete('fvid');
    retainedIds.forEach((id) => newParams.append('fvid', id));
    newParams.set('page', '1');
    submit(newParams);
  };

  return (
    <>
      <div className="w-full overflow-x-auto pb-4 mb-2 scrollbar-hide py-1 px-1">
        <div className="flex items-center gap-3 min-w-max h-12">
          {/* Sort Dropdown */}
          <div className="relative h-10 flex items-center">
            <select
              value={sort}
              onChange={(e) => handleFilterChange('sort', e.target.value)}
              className="appearance-none h-10 pl-5 pr-10 bg-[#F3F4F6] rounded-full text-sm font-medium border-none focus:ring-2 focus:ring-gray-200 cursor-pointer text-gray-700 outline-none flex items-center"
            >
              <option value="">{t('common.sortLatest')}</option>
              <option value="price">{t('common.sortPriceLowHigh')}</option>
              <option value="-price">{t('common.sortPriceHighLow')}</option>
            </select>
            <ChevronDown className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>

          {/* View Mode Toggle */}
          <button
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            className="flex items-center bg-[#F3F4F6] rounded-full p-1 h-10 cursor-pointer border-none outline-none focus:ring-2 focus:ring-gray-200"
            aria-label="Toggle View"
          >
            <div
              className={`p-1.5 rounded-full transition-all ${
                viewMode === 'grid'
                  ? 'bg-white shadow text-[#E80245]'
                  : 'text-gray-400'
              }`}
            >
              <LayoutGrid size={18} />
            </div>
            <div
              className={`p-1.5 rounded-full transition-all ${
                viewMode === 'list'
                  ? 'bg-white shadow text-[#E80245]'
                  : 'text-gray-400'
              }`}
            >
              <List size={18} />
            </div>
          </button>

          {/* Discount Toggle */}
          {discountFV && (
            <button
              onClick={() =>
                toggleFilterParam(
                  'discount_fvid',
                  discountFV.facetValue.id,
                  discountFvidParam,
                )
              }
              className={`h-10 px-5 rounded-full text-sm font-medium transition-colors border flex items-center ${
                discountFvidParam === discountFV.facetValue.id
                  ? 'bg-[#E80245] text-white border-[#E80245]'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
              }`}
            >
              {t('common.discounted')}
            </button>
          )}

          {/* Bulk Pricing Toggle */}
          {bulkFV && (
            <button
              onClick={() =>
                toggleFilterParam(
                  'bulk_fvid',
                  bulkFV.facetValue.id,
                  bulkFvidParam,
                )
              }
              className={`h-10 px-5 rounded-full text-sm font-medium transition-colors border flex items-center ${
                bulkFvidParam === bulkFV.facetValue.id
                  ? 'bg-[#E80245] text-white border-[#E80245]'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
              }`}
            >
              {t('common.bulkPricing')}
            </button>
          )}

          {supplierFacetValues.length > 0 && (
            <button
              onClick={clearSupplierFilter}
              className={`h-10 px-5 rounded-full text-sm font-medium border ${
                activeSupplierIds.length === 0
                  ? 'bg-[#E80245] text-white border-[#E80245]'
                  : 'bg-white text-gray-700 border-gray-200'
              }`}
            >
              All suppliers
            </button>
          )}
          {supplierFacetValues.map((value) => {
            const selected = activeSupplierIds.includes(value.facetValue.id);
            return (
              <button
                key={value.facetValue.id}
                onClick={() => toggleFacetValue(value.facetValue.id)}
                className={`h-10 px-5 rounded-full text-sm font-medium border ${
                  selected
                    ? 'bg-[#E80245] text-white border-[#E80245]'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                }`}
              >
                {value.facetValue.name}
              </button>
            );
          })}

          {/* Origin Dropdown (Custom Button) */}
          {originFacetValues.length > 0 && (
            <button
              ref={originButtonRef}
              onClick={() => setOriginOpen(!originOpen)}
              className={`h-10 pl-5 pr-4 rounded-full text-sm font-medium border flex items-center gap-2 transition-colors ${
                activeOriginCount > 0
                  ? 'bg-[#E80245] text-white border-[#E80245]'
                  : 'bg-[#F3F4F6] text-gray-700 border-transparent hover:border-gray-300'
              }`}
            >
              Origin {activeOriginCount > 0 && `(${activeOriginCount})`}
              <ChevronDown
                className={`w-4 h-4 ${
                  activeOriginCount > 0 ? 'text-white' : 'text-gray-500'
                }`}
              />
            </button>
          )}
        </div>
      </div>

      {/* Fixed Dropdown Portal */}
      {originOpen && originFacetValues.length > 0 && (
        <div
          id="origin-dropdown-portal"
          className="fixed z-[100] bg-white rounded-xl shadow-xl border border-gray-100 py-2 w-56 flex flex-col"
          style={{ top: dropdownPos.top, left: dropdownPos.left }}
        >
          {originFacetValues.map((fv) => {
            const isSelected = activeFacetValueIds.includes(fv.facetValue.id);
            return (
              <label
                key={fv.facetValue.id}
                className="flex items-center px-4 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors select-none"
              >
                <div
                  className={`w-5 h-5 rounded border flex items-center justify-center mr-3 transition-colors ${
                    isSelected
                      ? 'bg-[#E80245] border-[#E80245]'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                </div>
                <span
                  className={`text-sm ${
                    isSelected ? 'font-medium text-gray-900' : 'text-gray-700'
                  }`}
                >
                  {fv.facetValue.name}
                </span>
                <span className="ml-auto text-xs text-gray-400">
                  {fv.count}
                </span>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleFacetValue(fv.facetValue.id)}
                  className="hidden"
                />
              </label>
            );
          })}
        </div>
      )}
    </>
  );
}
